import {
	type ClusterPatchInput,
	ClusterPatchSchema,
	DEFAULT_COUNTRY_REGIONS,
	DEFAULT_REGION_FALLBACKS,
} from "@schemas";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@veil/ui/components/button";
import { Input } from "@veil/ui/components/input";
import { Label } from "@veil/ui/components/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@veil/ui/components/select";
import { Skeleton } from "@veil/ui/components/skeleton";
import { Textarea } from "@veil/ui/components/textarea";
import Globe from "lucide-react/dist/esm/icons/globe";
import HardDrive from "lucide-react/dist/esm/icons/hard-drive";
import Loader2 from "lucide-react/dist/esm/icons/loader-2";
import Lock from "lucide-react/dist/esm/icons/lock";
import Network from "lucide-react/dist/esm/icons/network";
import Server from "lucide-react/dist/esm/icons/server";
import ShieldCheck from "lucide-react/dist/esm/icons/shield-check";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
	TLS_CERTIFICATE_KEY,
	TlsCertDownload,
} from "@/components/tls-cert-download";
import { api, unwrap } from "@/lib/api";
import { IssuanceProgress } from "../setup/steps/issuance-progress";
import {
	ACME_COPY,
	getTlsConfig,
	type IssuanceCopy,
	ORIGIN_CA_COPY,
	postTls,
	type TlsConfig,
	type TlsMode,
} from "../setup/tls-api";
import {
	type TlsFormValue,
	TlsModeSelect,
	tlsModeLabel,
	useTlsForm,
} from "../setup/tls-controls";
import { ChipInput } from "./chip-input";
import { ConfigGroup } from "./config-group";
import {
	formatCountryRegions,
	formatRegionFallbacks,
	parseCountryRegions,
	parseRegionFallbacks,
} from "./geo-routing";
import { arraysEqual, type ClusterRow } from "./types";

const NO_WARNINGS: string[] = [];

/**
 * The cluster routing configuration panel (/cluster/routing). One
 * container, grouped rows, one staged save commit at the bottom —
 * editing the cluster row is a single PATCH. TLS lives on its own
 * sub-page (TlsSecurityCard, /cluster/security).
 */
export function ConfigPanel({
	cluster,
	canEdit,
	geoWarnings = NO_WARNINGS,
}: {
	cluster: ClusterRow;
	canEdit: boolean;
	geoWarnings?: string[];
}) {
	const [lbPolicy, setLbPolicy] = useState<ClusterRow["lbPolicy"]>(
		cluster.lbPolicy,
	);
	const [publicHostnames, setPublicHostnames] = useState<string[]>(
		cluster.publicHostnames,
	);
	const [releaseHostname, setReleaseHostname] = useState(
		cluster.releaseHostname ?? "",
	);
	const [webroot, setWebroot] = useState(cluster.webroot);
	const [extraAllowedIps, setExtraAllowedIps] = useState<string[]>(
		cluster.extraAllowedIps,
	);
	const [basicAuthRealm, setBasicAuthRealm] = useState(
		cluster.basicAuthRealm ?? "",
	);
	const [basicAuthFile, setBasicAuthFile] = useState(
		cluster.basicAuthFile ?? "",
	);
	const [managerHostname, setManagerHostname] = useState(
		cluster.managerHostname ?? "",
	);
	const initialCountryText = formatCountryRegions(cluster.countryRegions);
	const initialFallbackText = formatRegionFallbacks(cluster.regionFallbacks);
	const [countryText, setCountryText] = useState(initialCountryText);
	const [fallbackText, setFallbackText] = useState(initialFallbackText);
	const [launchSlots, setLaunchSlots] = useState(
		String(cluster.maxConcurrentLaunches),
	);
	const countryParsed = useMemo(
		() => parseCountryRegions(countryText),
		[countryText],
	);
	const fallbackParsed = useMemo(
		() => parseRegionFallbacks(fallbackText),
		[fallbackText],
	);
	const launchSlotsValue = Number(launchSlots);
	const launchSlotsValid =
		Number.isInteger(launchSlotsValue) &&
		launchSlotsValue >= 1 &&
		launchSlotsValue <= 100;

	const dirtyFields = useMemo(() => {
		const out: string[] = [];
		if (lbPolicy !== cluster.lbPolicy) out.push("Upstream policy");
		if (!arraysEqual(publicHostnames, cluster.publicHostnames))
			out.push("Public hostnames");
		if (releaseHostname !== (cluster.releaseHostname ?? ""))
			out.push("Release vhost");
		if (webroot !== cluster.webroot) out.push("Webroot");
		if (!arraysEqual(extraAllowedIps, cluster.extraAllowedIps))
			out.push("Allowed IPs");
		if (basicAuthRealm !== (cluster.basicAuthRealm ?? ""))
			out.push("Basic auth realm");
		if (basicAuthFile !== (cluster.basicAuthFile ?? ""))
			out.push(".htpasswd path");
		if (managerHostname !== (cluster.managerHostname ?? ""))
			out.push("Manager UI hostname");
		if (countryText !== initialCountryText) out.push("Country map");
		if (fallbackText !== initialFallbackText) out.push("Region fallbacks");
		if (launchSlots !== String(cluster.maxConcurrentLaunches))
			out.push("Launch slots per worker");
		return out;
	}, [
		lbPolicy,
		publicHostnames,
		releaseHostname,
		webroot,
		extraAllowedIps,
		basicAuthRealm,
		basicAuthFile,
		managerHostname,
		countryText,
		fallbackText,
		launchSlots,
		initialCountryText,
		initialFallbackText,
		cluster,
	]);

	const dirty = dirtyFields.length > 0;

	const queryClient = useQueryClient();
	const save = useMutation({
		mutationFn: (body: ClusterPatchInput) => {
			ClusterPatchSchema.parse(body);
			return unwrap(api.api.cluster.patch(body));
		},
		onSuccess: () => {
			toast.success("Configuration saved");
			queryClient.invalidateQueries({ queryKey: ["cluster"] });
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Save failed"),
	});

	const onSave = () => {
		if (!countryParsed.ok) {
			toast.error(`Country map: ${countryParsed.error}`);
			return;
		}
		if (!fallbackParsed.ok) {
			toast.error(`Region fallbacks: ${fallbackParsed.error}`);
			return;
		}
		if (!launchSlotsValid) {
			toast.error(
				"Launch slots per worker must be a whole number from 1 to 100",
			);
			return;
		}
		save.mutate({
			lbPolicy: lbPolicy as ClusterPatchInput["lbPolicy"],
			publicHostnames,
			releaseHostname: releaseHostname.trim() || null,
			webroot,
			extraAllowedIps,
			basicAuthRealm: basicAuthRealm.trim() || null,
			basicAuthFile: basicAuthFile.trim() || null,
			managerHostname: managerHostname.trim() || null,
			countryRegions: countryParsed.value,
			regionFallbacks: fallbackParsed.value,
			maxConcurrentLaunches: launchSlotsValue,
		});
	};

	const loadGeoDefaults = () => {
		setCountryText(formatCountryRegions(DEFAULT_COUNTRY_REGIONS));
		setFallbackText(formatRegionFallbacks(DEFAULT_REGION_FALLBACKS));
	};

	const onReset = () => {
		setLbPolicy(cluster.lbPolicy);
		setPublicHostnames(cluster.publicHostnames);
		setReleaseHostname(cluster.releaseHostname ?? "");
		setWebroot(cluster.webroot);
		setExtraAllowedIps(cluster.extraAllowedIps);
		setBasicAuthRealm(cluster.basicAuthRealm ?? "");
		setBasicAuthFile(cluster.basicAuthFile ?? "");
		setManagerHostname(cluster.managerHostname ?? "");
		setCountryText(initialCountryText);
		setFallbackText(initialFallbackText);
		setLaunchSlots(String(cluster.maxConcurrentLaunches));
	};

	return (
		<div className="overflow-hidden rounded-xl border border-border/70 bg-card shadow-sm">
			<header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-border/60 px-6 py-4 md:px-8">
				<div>
					<div className="font-mono text-[0.68rem] uppercase tracking-[0.16em] text-muted-foreground">
						Section
					</div>
					<h2 className="font-heading text-base font-semibold tracking-tight">
						Routing configuration
					</h2>
				</div>
				<p className="text-xs leading-relaxed text-muted-foreground">
					Edits stage locally. Save commits to the database; Render &amp; Reload
					pushes the result to nginx.
				</p>
			</header>

			<div className="divide-y divide-border/60">
				<ConfigGroup
					title="Public routing"
					icon={Network}
					description="Hostnames the LB proxies and how upstream picks the next worker."
				>
					<div className="flex flex-col gap-2">
						<Label>Upstream policy</Label>
						<Select
							value={lbPolicy}
							onValueChange={canEdit ? setLbPolicy : undefined}
							disabled={!canEdit}
						>
							<SelectTrigger className="h-9 w-full max-w-sm px-3">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="round-robin">round-robin</SelectItem>
								<SelectItem value="least-conn">least-conn</SelectItem>
							</SelectContent>
						</Select>
						<p className="text-xs leading-relaxed text-muted-foreground">
							Emitted as the directive inside{" "}
							<code className="rounded bg-muted px-1 font-mono text-[0.72rem]">
								upstream backend_servers
							</code>
							.
						</p>
					</div>
					<div className="flex flex-col gap-2">
						<Label>Public hostnames (LB-proxied)</Label>
						<ChipInput
							values={publicHostnames}
							onChange={setPublicHostnames}
							disabled={!canEdit}
							placeholder="mlx.publicuppers.com"
						/>
						<p className="text-xs leading-relaxed text-muted-foreground">
							Added to the lua{" "}
							<code className="rounded bg-muted px-1 font-mono text-[0.72rem]">
								valid_domains
							</code>{" "}
							table. Press <kbd className="font-mono text-[0.72rem]">Enter</kbd>{" "}
							or <kbd className="font-mono text-[0.72rem]">,</kbd> to add.
						</p>
					</div>
				</ConfigGroup>

				<ConfigGroup
					title="Geo routing"
					icon={Globe}
					description={
						<>
							Clients choose a pool for{" "}
							<code className="rounded bg-muted px-1 font-mono text-[0.72rem]">
								/launch
							</code>{" "}
							with{" "}
							<code className="rounded bg-muted px-1 font-mono text-[0.72rem]">
								X-Veil-Region
							</code>{" "}
							or{" "}
							<code className="rounded bg-muted px-1 font-mono text-[0.72rem]">
								X-Veil-Country
							</code>
							. Active once cluster routing is on and any enabled worker has a
							region.
						</>
					}
					right={
						canEdit ? (
							<Button variant="outline" size="sm" onClick={loadGeoDefaults}>
								Load defaults
							</Button>
						) : null
					}
				>
					<div className="flex flex-col gap-2">
						<Label htmlFor="cf-country-regions">Country map</Label>
						<Textarea
							id="cf-country-regions"
							className="min-h-24 px-3 py-2.5 font-mono text-sm"
							rows={4}
							value={countryText}
							onChange={(e) => setCountryText(e.target.value)}
							placeholder={"de: AT, DE, FR, GB\nus-east: US, BR"}
							disabled={!canEdit}
							aria-invalid={!countryParsed.ok}
						/>
						<p className="text-xs leading-relaxed text-muted-foreground">
							One line per region:{" "}
							<code className="rounded bg-muted px-1 font-mono text-[0.72rem]">
								region: country, country
							</code>
							. Case-insensitive; UK is treated as GB. Unmapped countries use
							every enabled worker.
						</p>
						{!countryParsed.ok && (
							<p className="text-xs text-destructive">{countryParsed.error}</p>
						)}
					</div>
					<div className="flex flex-col gap-2">
						<Label htmlFor="cf-region-fallbacks">Region fallbacks</Label>
						<Textarea
							id="cf-region-fallbacks"
							className="min-h-20 px-3 py-2.5 font-mono text-sm"
							rows={3}
							value={fallbackText}
							onChange={(e) => setFallbackText(e.target.value)}
							placeholder={"de: fi, us-east\nus-east: us-west, de"}
							disabled={!canEdit}
							aria-invalid={!fallbackParsed.ok}
						/>
						<p className="text-xs leading-relaxed text-muted-foreground">
							Tried in order when every worker in a region is full or the region
							has none. If all are full, the launch queues on the least-busy
							worker of the first region.
						</p>
						{!fallbackParsed.ok && (
							<p className="text-xs text-destructive">{fallbackParsed.error}</p>
						)}
					</div>
					<div className="flex flex-col gap-2">
						<Label htmlFor="cf-launch-slots">Launch slots per worker</Label>
						<Input
							id="cf-launch-slots"
							type="number"
							min={1}
							max={100}
							className="h-9 w-32 px-3"
							value={launchSlots}
							onChange={(e) => setLaunchSlots(e.target.value)}
							disabled={!canEdit}
							aria-invalid={!launchSlotsValid}
						/>
						<p className="text-xs leading-relaxed text-muted-foreground">
							A worker counts as full once this many launches have started on it
							within the last few seconds. Running sessions do not count; that
							limit is the worker's own{" "}
							<code className="rounded bg-muted px-1 font-mono text-[0.72rem]">
								VEIL_MAX_BROWSERS
							</code>
							.
						</p>
					</div>
					{geoWarnings.length > 0 && (
						<ul className="space-y-1 rounded-lg border border-amber-500/30 bg-amber-50/70 px-4 py-3 text-xs leading-relaxed text-amber-800 dark:bg-amber-500/[0.06] dark:text-amber-300">
							{geoWarnings.map((w) => (
								<li key={w}>{w}</li>
							))}
						</ul>
					)}
				</ConfigGroup>

				<ConfigGroup
					title="Release origin"
					icon={HardDrive}
					description="The vhost that serves /var/www/release and the allow-list that gates it."
				>
					<div className="flex flex-col gap-2">
						<Label htmlFor="cf-release-hostname">
							Release vhost server_name
						</Label>
						<Input
							id="cf-release-hostname"
							className="h-9 max-w-md px-3"
							value={releaseHostname}
							onChange={(e) => setReleaseHostname(e.target.value)}
							placeholder="release.publicuppers.com"
							disabled={!canEdit}
						/>
					</div>
					<div className="flex flex-col gap-2">
						<Label htmlFor="cf-webroot">Webroot</Label>
						<Input
							id="cf-webroot"
							className="h-9 max-w-md px-3 font-mono text-sm"
							value={webroot}
							onChange={(e) => setWebroot(e.target.value)}
							disabled={!canEdit}
						/>
					</div>
					<div className="flex flex-col gap-2">
						<Label>Extra allowed IPs</Label>
						<ChipInput
							values={extraAllowedIps}
							onChange={setExtraAllowedIps}
							disabled={!canEdit}
							placeholder="5.78.86.105"
						/>
						<p className="text-xs leading-relaxed text-muted-foreground">
							Added to{" "}
							<code className="rounded bg-muted px-1 font-mono text-[0.72rem]">
								allowed_ips
							</code>{" "}
							beyond every worker's public IP.
						</p>
					</div>
				</ConfigGroup>

				<ConfigGroup
					title="Basic auth"
					icon={Lock}
					description="Gate the release vhost behind nginx auth_basic. Leave both blank to disable."
				>
					<div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2">
						<div className="flex flex-col gap-2">
							<Label htmlFor="cf-realm">Realm</Label>
							<Input
								id="cf-realm"
								className="h-9 px-3"
								value={basicAuthRealm}
								onChange={(e) => setBasicAuthRealm(e.target.value)}
								placeholder="VeilBrowser Release"
								disabled={!canEdit}
							/>
						</div>
						<div className="flex flex-col gap-2">
							<Label htmlFor="cf-htpasswd">.htpasswd path</Label>
							<Input
								id="cf-htpasswd"
								className="h-9 px-3 font-mono text-sm"
								value={basicAuthFile}
								onChange={(e) => setBasicAuthFile(e.target.value)}
								placeholder="/usr/local/openresty/nginx/conf/.htpasswd"
								disabled={!canEdit}
							/>
						</div>
					</div>
				</ConfigGroup>

				<ConfigGroup
					title="Manager UI"
					icon={Server}
					description="Optional hostname OpenResty reverse-proxies to 127.0.0.1:4180."
				>
					<div className="flex flex-col gap-2">
						<Label htmlFor="cf-manager-hostname">Manager UI hostname</Label>
						<Input
							id="cf-manager-hostname"
							className="h-9 max-w-md px-3"
							value={managerHostname}
							onChange={(e) => setManagerHostname(e.target.value)}
							placeholder="cluster-dev.publicuppers.com"
							disabled={!canEdit}
						/>
						<p className="text-xs leading-relaxed text-muted-foreground">
							Leave blank to keep the UI on the raw ip:4180 link only.
						</p>
					</div>
				</ConfigGroup>
			</div>

			{canEdit && dirty && (
				<StagedSaveBar
					pending={save.isPending}
					fields={dirtyFields}
					onSave={onSave}
					onReset={onReset}
				/>
			)}
		</div>
	);
}

function StagedSaveBar({
	pending,
	fields,
	onSave,
	onReset,
}: {
	pending: boolean;
	fields: string[];
	onSave: () => void;
	onReset: () => void;
}) {
	return (
		<footer className="flex flex-wrap items-center justify-between gap-3 border-t border-amber-500/30 bg-amber-50/70 px-6 py-4 dark:bg-amber-500/[0.06] md:px-8">
			<div className="min-w-0 max-w-[min(100%,36rem)]">
				<div className="font-mono text-[0.68rem] uppercase tracking-[0.16em] text-amber-700 dark:text-amber-400">
					{fields.length} unsaved {fields.length === 1 ? "field" : "fields"}
				</div>
				<p className="truncate text-xs leading-relaxed text-foreground/80">
					{fields.join(", ")}
				</p>
			</div>
			<div className="flex items-center gap-2">
				<Button variant="ghost" size="sm" onClick={onReset} disabled={pending}>
					Reset
				</Button>
				<Button onClick={onSave} disabled={pending} size="sm">
					{pending && (
						<Loader2 data-icon="inline-start" className="animate-spin" />
					)}
					Save
				</Button>
			</div>
		</footer>
	);
}

// ---------------------------------------------------------------------------
// Security & access — TLS model editing (post-setup). Wired to the TLS route
// (POST /api/system/tls), not the staged cluster PATCH, since applying a mode
// writes certs + re-renders nginx and can kick a streamed issuance Operation.
// ---------------------------------------------------------------------------

function normalizeTlsMode(mode: string): TlsMode {
	return mode === "acme" || mode === "byo" ? mode : "cloudflare";
}

export function TlsSecurityCard({
	cluster,
	canEdit,
}: {
	cluster: ClusterRow;
	canEdit: boolean;
}) {
	const config = useQuery({
		queryKey: ["tls-config"],
		queryFn: getTlsConfig,
		staleTime: 5_000,
		retry: false,
	});

	return (
		<div className="overflow-hidden rounded-xl border border-border/70 bg-card shadow-sm">
			<header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-border/60 px-6 py-4 md:px-8">
				<div>
					<div className="font-mono text-[0.68rem] uppercase tracking-[0.16em] text-muted-foreground">
						Section
					</div>
					<h2 className="font-heading text-base font-semibold tracking-tight">
						Security &amp; access
					</h2>
				</div>
				<p className="text-xs leading-relaxed text-muted-foreground">
					How HTTPS is terminated, the origin certificate, and the Cloudflare
					origin lock. Applying re-renders nginx.
				</p>
			</header>

			<div className="divide-y divide-border/60">
				{config.isLoading || !config.data ? (
					<div className="px-6 py-7 md:px-8">
						<Skeleton className="h-40" />
					</div>
				) : (
					<TlsSecurityForm
						initial={config.data}
						cluster={cluster}
						canEdit={canEdit}
					/>
				)}
			</div>
		</div>
	);
}

function TlsSecurityForm({
	initial,
	cluster,
	canEdit,
}: {
	initial: TlsConfig;
	cluster: ClusterRow;
	canEdit: boolean;
}) {
	const queryClient = useQueryClient();
	const hostname = cluster.publicHostnames[0] ?? null;
	const hostnameAvailable = cluster.publicHostnames.length > 0;

	// Full snapshot of the persisted config, used to seed the form and to
	// reset back to it. Token/key/cert/zone-CA are always blank (never stored).
	const base = useMemo<TlsFormValue>(
		() => ({
			mode: normalizeTlsMode(initial.tlsMode),
			cfMode: initial.cfMode,
			fullCertSource: initial.cfFullCertSource,
			originLock: initial.cfOriginLock,
			mtlsEnabled: initial.originMtlsEnabled,
			zoneCaPem: "",
			originCaToken: "",
			email: "",
			cert: "",
			key: "",
		}),
		[initial],
	);

	const { form, value, willIssue, buildRequest } = useTlsForm(base);
	const [operationId, setOperationId] = useState<string | null>(null);
	const [operationCopy, setOperationCopy] = useState<IssuanceCopy>(ACME_COPY);
	const [opStatus, setOpStatus] = useState("");

	const saving = form.formState.isSubmitting;
	const dirty = form.formState.isDirty;

	const hasOriginCert = !(
		value.mode === "cloudflare" && value.cfMode === "flexible"
	);

	const apply = form.handleSubmit(async (values) => {
		setOpStatus("");
		try {
			const res = await postTls(buildRequest());
			queryClient.invalidateQueries({ queryKey: ["cluster"] });
			queryClient.invalidateQueries({ queryKey: ["tls-config"] });
			queryClient.invalidateQueries({ queryKey: TLS_CERTIFICATE_KEY });
			if (res.operationId) {
				setOperationCopy(values.mode === "acme" ? ACME_COPY : ORIGIN_CA_COPY);
				setOperationId(res.operationId);
			} else {
				toast.success(`TLS set to ${tlsModeLabel(values)}`);
				// Re-baseline: keep the applied choices, blank the secrets (they are
				// never stored server-side), clear the dirty flag.
				form.reset({
					...values,
					zoneCaPem: "",
					originCaToken: "",
					email: "",
					cert: "",
					key: "",
				});
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Could not set TLS");
		}
	});

	const retry = () => {
		setOperationId(null);
		void apply();
	};

	const reset = () => {
		form.reset(base);
		setOperationId(null);
		setOpStatus("");
	};

	const succeeded = opStatus === "succeeded";
	const issuing = !!operationId && !succeeded && opStatus !== "failed";

	return (
		<ConfigGroup
			title="TLS mode"
			icon={ShieldCheck}
			description={
				<>
					Current mode:{" "}
					<span className="font-medium text-foreground">
						{tlsModeLabel(base)}
					</span>
					.
				</>
			}
		>
			<TlsModeSelect
				form={form}
				hostname={hostname}
				hostnameAvailable={hostnameAvailable}
				idPrefix="settings-tls"
				disabled={!canEdit || issuing}
			/>

			{hasOriginCert && !operationId && (
				<TlsCertDownload enabled={hasOriginCert} />
			)}

			{operationId && (
				<IssuanceProgress
					operationId={operationId}
					hostname={hostname}
					copy={operationCopy}
					onStatusChange={setOpStatus}
					onRetry={retry}
					retrying={saving}
				/>
			)}

			{canEdit && (
				<div className="flex items-center justify-end gap-2">
					{dirty && (
						<Button
							variant="ghost"
							size="sm"
							onClick={reset}
							disabled={saving || issuing}
						>
							Reset
						</Button>
					)}
					<Button
						size="sm"
						onClick={apply}
						disabled={saving || issuing || !dirty}
					>
						{saving && (
							<Loader2 data-icon="inline-start" className="animate-spin" />
						)}
						{willIssue
							? "Apply & issue certificate"
							: "Apply security settings"}
					</Button>
				</div>
			)}
		</ConfigGroup>
	);
}
